import { Currency } from 'nomst/Stores/Utility/currency';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import * as formatParse from 'common/format-parse';
import { Format } from './format';
import { observable, computed, action, makeObservable } from 'mobx';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { getActiveConfiguration } from 'nomst/Actions/configurations';

export class CurrencyConversion {

    private _currencies: Map<string, Currency>;
    @observable selectedCurrency: string;

    constructor(
        currencies: Nullable<Currency[]>) {
        makeObservable(this);
        currencies = Coalesce.array(currencies || []);
        this._currencies = new Map(currencies.map(e => [this.getKey(e.source, e.target), e] as [string, Currency]));
        this.selectedCurrency = '';
    }

    @computed
    get configuration(): Configuration {
        return getActiveConfiguration();
    }

    @computed
    get useMultipleCurrency(): boolean {
        return !this.selectedCurrency || this.selectedCurrency.length === 0;
    }

    @computed
    get hasMultipleCurrency(): boolean {
        if (!this.configuration) {
            return false;
        }

        return this.configuration.hasMultipleCurrency;
    }

    @computed
    get homeCountryCurrency(): string {
        if (!this.configuration) {
            return '';
        }

        return this.configuration.homeCountryCurrency;
    }

    @computed
    get availableCurrencyOptions(): IOption[] {
        const availableCurrencyOptions = [{
            text: 'Multiple',
            value: ''
        }];

        
        const allCurrencies = new Set(Array.from(this._currencies.values()).map(currency => {
            return currency.source;
        }));

        availableCurrencyOptions.push(...Array.from(allCurrencies).map(currency => ({
            text: currency,
            value: currency
        })));

        return availableCurrencyOptions;
    }

    @action
    resetCurrencySelection(): void {
        this.selectedCurrency = '';
    }

    @action
    setCurrency(currency: string): void {
        this.selectedCurrency = currency;
    }

    convertCurrency(source: Nullable<string>, target: Nullable<string>, value: number): number {
        if (!source || !target || source === target) {
            return value;
        }
        const key = this.getKey(source, target);
        if (this._currencies.has(key) === false) {
            return value;
        }

        const currency = this._currencies.get(key);

        if (!currency) {
            return value;
        }

        return value * currency.rate;
    }

    convertCurrencyToTextDisplay(
        sourceCurrency: string,
        format: Format,
        value: number,
        useMultipleCurrency: boolean,
        currentCurrency: string,
        isHourly: boolean = false,
        showPerHour: boolean = true): string {
        const valueText = this.convertCurrencyToDisplay(
            sourceCurrency,
            format,
            value,
            useMultipleCurrency,
            currentCurrency,
            isHourly);
        return (isHourly && showPerHour) ? formatParse.formatHourly(valueText) : valueText;
    }

    convertCurrencyFromDisplay(
        sourceCurrency: string,
        value: number,
        useMultipleCurrency: boolean,
        currentCurrency: string): number {
        if (useMultipleCurrency || this._currencies.has(sourceCurrency)) {
            return value;
        }

        return this.convertCurrency(sourceCurrency, currentCurrency, value);
    }

    convertCurrencyToDisplay(
        sourceCurrency: string,
        format: Format,
        value: number,
        useMultipleCurrency: boolean,
        currentCurrency: string,
        isHourly: boolean = false): string {
        if (useMultipleCurrency || this._currencies.has(sourceCurrency)) {
            return format.formatCurrencyValue(sourceCurrency, value, isHourly);
        }

        const newValue = this.convertCurrency(sourceCurrency, currentCurrency, value);
        return format.formatCurrencyValue(currentCurrency, newValue, isHourly);
    }

    private getKey(source: string, target: string): string {
        return source + '-' + target;
    }
}